import { Suspense, lazy } from "react";
import type { PaywallPresentationProps } from "./steps/PaymentStep";
import type { AnswersByKey, FlowVariant, FunnelId, FunnelStep } from "../funnel/types";
import { getPersonaDataWithOverride, resolvePersonaOverrideFromUrl } from "../funnel/flowData";
import { preloadStoryCovers } from "../funnel/storyRecommendations";

type StepRendererProps = {
  step: FunnelStep;
  funnelId: FunnelId;
  paywall: PaywallPresentationProps;
  answers: AnswersByKey;
  onSelectSingle: (answerKey: string, optionId: string) => void;
  onToggleMulti: (answerKey: string, optionId: string) => void;
  onSetText: (answerKey: string, value: string) => void;
  onWelcomeFirstImageReadyChange?: (isReady: boolean) => void;
  onLoadingContinue: () => void;
  onPaymentContinue: () => void;
  isPaymentSubmitting: boolean;
  showPaymentForm: boolean;
  paymentFormProps?: {
    planId: string;
    flow: FlowVariant;
    email?: string;
    promoCode?: string;
    currency?: string;
    value?: number;
    quotedMinor?: number;
    eventId?: string;
    fbp?: string;
    fbc?: string;
    onPaymentSuccess: (subscriptionId: string) => void;
    onReady?: () => void;
  };
};

const loadQuestionStep = () => import("./steps/QuestionStep");
const loadAffirmationStep = () => import("./steps/AffirmationStep");
const loadCheckoutCompleteStep = () => import("./steps/CheckoutCompleteStep");
const loadLoadingStep = () => import("./steps/LoadingStep");
const loadResultStep = () => import("./steps/ResultStep");
const loadJourneyFeatureStep = () => import("./steps/JourneyFeatureStep");
const loadEmailStep = () => import("./steps/EmailStep");
const loadPaymentStep = () => import("./steps/PaymentStep");
const loadStoryRecommendationStep = () => import("./steps/StoryRecommendationStep");

const QuestionStepLazy = lazy(async () => ({ default: (await loadQuestionStep()).QuestionStep }));
const AffirmationStepLazy = lazy(async () => ({
  default: (await loadAffirmationStep()).AffirmationStep
}));
const CheckoutCompleteStepLazy = lazy(async () => ({
  default: (await loadCheckoutCompleteStep()).CheckoutCompleteStep
}));
const LoadingStepLazy = lazy(async () => ({ default: (await loadLoadingStep()).LoadingStep }));
const ResultStepLazy = lazy(async () => ({ default: (await loadResultStep()).ResultStep }));
const JourneyFeatureStepLazy = lazy(async () => ({
  default: (await loadJourneyFeatureStep()).JourneyFeatureStep
}));
const EmailStepLazy = lazy(async () => ({ default: (await loadEmailStep()).EmailStep }));
const PaymentStepLazy = lazy(async () => ({ default: (await loadPaymentStep()).PaymentStep }));
const StoryRecommendationStepLazy = lazy(async () => ({
  default: (await loadStoryRecommendationStep()).StoryRecommendationStep
}));

const LaurelIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="36"
    height="52"
    viewBox="0 0 36 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M 22 48 Q 8 26 18 4" stroke="#c9974a" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    <path d="M 18.0 40.7 Q 15.8 36.9 11.5 36.0 Q 13.7 39.8 18.0 40.7 Z" fill="#c9974a" />
    <path d="M 18.0 40.7 Q 20.2 36.9 18.8 32.7 Q 16.6 36.5 18.0 40.7 Z" fill="#c9974a" />
    <path d="M 15.3 33.3 Q 13.8 29.2 9.7 27.7 Q 11.2 31.8 15.3 33.3 Z" fill="#c9974a" />
    <path d="M 15.3 33.3 Q 18.1 29.9 17.4 25.6 Q 14.6 29.0 15.3 33.3 Z" fill="#c9974a" />
    <path d="M 14.0 26.0 Q 13.2 21.7 9.4 19.5 Q 10.2 23.8 14.0 26.0 Z" fill="#c9974a" />
    <path d="M 14.0 26.0 Q 17.3 23.1 17.4 18.7 Q 14.0 21.6 14.0 26.0 Z" fill="#c9974a" />
    <path d="M 14.0 18.7 Q 14.0 14.3 10.6 11.4 Q 10.7 15.8 14.0 18.7 Z" fill="#c9974a" />
    <path d="M 14.0 18.7 Q 17.8 16.4 18.6 12.1 Q 14.8 14.4 14.0 18.7 Z" fill="#c9974a" />
    <path d="M 15.3 11.3 Q 16.1 7.0 13.3 3.6 Q 12.6 7.9 15.3 11.3 Z" fill="#c9974a" />
    <path d="M 15.3 11.3 Q 19.4 9.8 21.0 5.7 Q 16.9 7.2 15.3 11.3 Z" fill="#c9974a" />
    <path d="M 18.0 4.0 Q 21.5 0.6 21.7 -4.2 Q 18.2 -0.8 18.0 4.0 Z" fill="#c9974a" />
  </svg>
);

export const preloadStepRendererChunk = (kind: FunnelStep["kind"] | undefined): Promise<unknown> => {
  switch (kind) {
    case "question":
      return loadQuestionStep();
    case "affirmation":
      return Promise.all([loadAffirmationStep(), loadCheckoutCompleteStep()]);
    case "loading":
      return loadLoadingStep();
    case "result":
      return loadResultStep();
    case "journey_feature":
      return loadJourneyFeatureStep();
    case "email":
      return loadEmailStep();
    case "payment":
      return loadPaymentStep();
    case "story_recommendation":
      return loadStoryRecommendationStep();
    case "welcome":
    case undefined:
      return Promise.resolve();
  }
};

const getAnswerKeyForStep = (step: FunnelStep): string => {
  if (step.kind === "question" && step.answerKey) {
    return step.answerKey;
  }
  if (step.kind === "email") {
    return step.answerKey;
  }
  if (step.kind === "payment") {
    return step.answerKey;
  }
  return step.id;
};

export const StepRenderer = ({
  step,
  funnelId,
  paywall,
  answers,
  onSelectSingle,
  onToggleMulti,
  onSetText,
  onWelcomeFirstImageReadyChange,
  onLoadingContinue,
  onPaymentContinue,
  isPaymentSubmitting,
  showPaymentForm,
  paymentFormProps
}: StepRendererProps) => {
  if (step.kind === "welcome") {
    const [ratingLead, ratingTail] = step.ratingText.split("·").map((part) => part.trim());

    return (
      <section className="step step-welcome">
        <div className="welcome-intro-spacer" />
        <div className="welcome-intro">
          <h1 className="welcome-headline" dangerouslySetInnerHTML={{ __html: step.title }} />
          <div className="welcome-trust-badge">
            <LaurelIcon className="welcome-laurel welcome-laurel-left" />
            <div className="welcome-trust-text">
              <span className="welcome-trust-label">Trusted by</span>
              <strong className="welcome-trust-count">{ratingLead}</strong>
              <span className="welcome-trust-desc">{ratingTail}</span>
            </div>
            <LaurelIcon className="welcome-laurel welcome-laurel-right" />
          </div>
        </div>
        {step.heroImage ? (
          <div className="welcome-hero-static">
            {step.heroVideo ? (
              /* The poster is frame 0 of the clip and is what the pre-hydration
                 shell paints, so taking over with the video cannot cause a jump. */
              <video
                className="welcome-hero-image welcome-hero-video"
                poster={step.heroImage}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true"
                onCanPlay={() => onWelcomeFirstImageReadyChange?.(true)}
                onError={() => onWelcomeFirstImageReadyChange?.(true)}
              >
                <source src={step.heroVideo.webm} type="video/webm" />
                <source src={step.heroVideo.mp4} type="video/mp4" />
              </video>
            ) : (
              <img
                src={step.heroImage}
                alt=""
                className="welcome-hero-image"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                onLoad={() => onWelcomeFirstImageReadyChange?.(true)}
                onError={() => onWelcomeFirstImageReadyChange?.(true)}
              />
            )}
          </div>
        ) : null}
        {step.topicsText ? <div className="welcome-topics-text">{step.topicsText}</div> : null}
      </section>
    );
  }

  if (step.kind === "question") {
    const answerKey = getAnswerKeyForStep(step);
    const value = answers[answerKey];
    return (
      <Suspense fallback={null}>
        <QuestionStepLazy
          step={step}
          value={typeof value === "string" || Array.isArray(value) ? value : undefined}
          onSelectSingle={(optionId) => onSelectSingle(answerKey, optionId)}
          onToggleMulti={(optionId) => onToggleMulti(answerKey, optionId)}
        />
      </Suspense>
    );
  }

  if (step.kind === "affirmation") {
    return (
      <Suspense fallback={null}>
        {step.id === "checkout_complete" ? (
          <CheckoutCompleteStepLazy step={step} />
        ) : (
          <AffirmationStepLazy step={step} />
        )}
      </Suspense>
    );
  }

  if (step.kind === "loading") {
    const modalValue = answers[step.modalQuestion.answerKey];
    return (
      <Suspense fallback={null}>
        <LoadingStepLazy
          step={step}
          modalValue={typeof modalValue === "string" ? modalValue : undefined}
          onSelectModal={(optionId) => onSelectSingle(step.modalQuestion.answerKey, optionId)}
          onContinue={onLoadingContinue}
        />
      </Suspense>
    );
  }

  if (step.kind === "result") {
    const resolvedStep = step.useComputedPersona === false
      ? step
      : { ...step, ...getPersonaDataWithOverride(answers, resolvePersonaOverrideFromUrl()) };
    return (
      <Suspense fallback={null}>
        <ResultStepLazy step={resolvedStep} />
      </Suspense>
    );
  }

  if (step.kind === "journey_feature") {
    preloadStoryCovers(answers);
    return (
      <Suspense fallback={null}>
        <JourneyFeatureStepLazy step={step} answers={answers} funnelId={funnelId} />
      </Suspense>
    );
  }

  if (step.kind === "email") {
    const answerKey = getAnswerKeyForStep(step);
    const email = answers[answerKey];
    return (
      <Suspense fallback={null}>
        <EmailStepLazy
          step={step}
          email={typeof email === "string" ? email : ""}
          onEmailChange={(value) => onSetText(answerKey, value)}
        />
      </Suspense>
    );
  }

  if (step.kind === "story_recommendation") {
    preloadStoryCovers(answers);
    const answerKey = getAnswerKeyForStep(step);
    return (
      <Suspense fallback={null}>
        <StoryRecommendationStepLazy
          step={step}
          answers={answers}
          onMarkCarouselViewed={() => onSetText(answerKey, "viewed")}
        />
      </Suspense>
    );
  }

  const answerKey = getAnswerKeyForStep(step);
  const selectedPlan = answers[answerKey];

  return (
    <Suspense fallback={null}>
      <PaymentStepLazy
        step={step}
        answers={answers}
        funnelId={funnelId}
        {...paywall}
        selectedPlan={typeof selectedPlan === "string" ? selectedPlan : ""}
        onSelectPlan={(planId) => onSetText(answerKey, planId)}
        onProceedPayment={onPaymentContinue}
        isSubmitting={isPaymentSubmitting}
        showPaymentForm={showPaymentForm}
        paymentFormProps={paymentFormProps}
      />
    </Suspense>
  );
};
